<template>
    <div class="camera-container border-2 border-dashed border-gray-300 py-4" style="border-radius: 0.5em">
        <div class="camera-button">
            <button
                type="button"
                class="button is-rounded w-full text-sm"
                :class="{ 'is-primary': !isCameraOpen, 'is-danger': isCameraOpen }"
                @click="toggleCamera">
                <span v-if="!isCameraOpen" class="text-gray-600">{{
                    i18n.global.t('camera.click_here_to_capture_image')
                }}</span>
                <span v-else class="text-gray-600">{{ i18n.global.t('camera.close') }}</span>
            </button>
        </div>

        <div v-if="isCameraOpen" v-show="!isLoading" class="camera-box" :class="{ flash: isShotPhoto }">
            <div class="camera-shutter" :class="{ flash: isShotPhoto }"></div>

            <video
                v-show="!isPhotoTaken"
                ref="camera"
                class="w-full"
                :width="isMobileDevice ? 567 : 756"
                :height="isMobileDevice ? 756 : 567"
                autoplay></video>

            <canvas
                v-show="isPhotoTaken"
                :id="canvasId"
                ref="canvas"
                class="w-full"
                :width="isMobileDevice ? 567 : 756"
                :height="isMobileDevice ? 756 : 567"></canvas>
        </div>

        <div v-if="isCameraOpen && !isLoading" class="camera-shoot text-center" style="margin-top: -5em">
            <button type="button" @click="takePhoto">
                <span class="fa-stack fa-2x">
                    <i class="fa fa-circle fa-stack-2x" style="color: white"></i>
                    <i
                        :class="`fa ${isPhotoTaken ? 'fa-trash' : 'fa-camera'} fa-stack-1x icon-color`"
                        aria-hidden="true"></i>
                </span>
            </button>

            <button v-if="options.length > 1 && !isPhotoTaken" type="button" @click="toggleCamera(true)">
                <span class="fa-stack fa-2x">
                    <i class="fa fa-circle fa-stack-2x" style="color: white"></i>
                    <svg
                        fill="rgb(41, 44, 50)"
                        xmlns="http://www.w3.org/2000/svg"
                        class="fa fa-stack-1x icon-color"
                        viewBox="-150 -200 950 700"
                        aria-hidden="true">
                        <path
                            d="M213.1 64.8L202.7 96 128 96c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-256c0-35.3-28.7-64-64-64l-74.7 0L426.9 64.8C420.4 45.2 402.1 32 381.4 32L258.6 32c-20.7 0-39 13.2-45.5 32.8zM448 256c0 8.8-7.2 16-16 16l-76.7 0c-6.2 0-11.3-5.1-11.3-11.3c0-3 1.2-5.9 3.3-8L371 229c-13.6-13.4-31.9-21-51-21c-19.2 0-37.7 7.6-51.3 21.3L249 249c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l19.7-19.7C257.4 172.7 288 160 320 160c31.8 0 62.4 12.6 85 35l23.7-23.7c2.1-2.1 5-3.3 8-3.3c6.2 0 11.3 5.1 11.3 11.3l0 76.7zM192 320c0-8.8 7.2-16 16-16l76.7 0c6.2 0 11.3 5.1 11.3 11.3c0 3-1.2 5.9-3.3 8L269 347c13.6 13.4 31.9 21 51 21c19.2 0 37.7-7.6 51.3-21.3L391 327c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-19.7 19.7C382.6 403.3 352 416 320 416c-31.8 0-62.4-12.6-85-35l-23.7 23.7c-2.1 2.1-5 3.3-8 3.3c-6.2 0-11.3-5.1-11.3-11.3l0-76.7z" />
                    </svg>
                </span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { postUploadKycDocumentS3 } from '../../utilities/api'
import { i18n } from '../../localization'
const emit = defineEmits(['update:modelValue', 'update:inputSource'])

const isCameraOpen = ref(false)
const isPhotoTaken = ref(false)
const isShotPhoto = ref(false)
const isLoading = ref(false)
const camera = ref(null)
const canvasId = `canvas_${(Math.random() + 1).toString(36).substring(2)}` // for randomized canvas id-s, otherwise the captured image always will be the first canvas (if there is multiple on same page)
const canvas = ref(canvasId)
const currentCameraIndex = ref(0)
const options = ref([])
const isMobileDevice = isMobile()

onMounted(() => {
    navigator?.mediaDevices?.enumerateDevices().then((mediaDevices) => gotDevices(mediaDevices))
})

function gotDevices(mediaDevices) {
    mediaDevices.forEach((mediaDevice) => {
        if (mediaDevice.kind === 'videoinput') {
            options.value.push({
                deviceId: mediaDevice.deviceId,
            })
        }
    })
}

function toggleCamera(changeMode = false) {
    if (changeMode === true) {
        isCameraOpen.value = true
        createCameraElement(changeMode)
        emit('update:inputSource', 'camera')
    } else if (isCameraOpen.value) {
        isCameraOpen.value = false
        isPhotoTaken.value = false
        isShotPhoto.value = false
        stopCameraStream()
        emit('update:inputSource', false)
    } else {
        isCameraOpen.value = true
        createCameraElement(false)
        emit('update:inputSource', 'camera')
    }
}

function createCameraElement(changeMode = false) {
    isLoading.value = true

    let constraints = (window.constraints = {
        audio: false,
        video: {
            facingMode: 'environment',
        },
    })

    if (changeMode) {
        currentCameraIndex.value = currentCameraIndex.value + 1
        if (currentCameraIndex.value >= options.value.length) {
            currentCameraIndex.value = 0
        }

        constraints = window.constraints = {
            audio: false,
            video: {
                deviceId: { exact: options.value[currentCameraIndex.value].deviceId },
            },
        }
    }

    navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
            isLoading.value = false
            camera.value.srcObject = stream
        })
        .catch((error) => {
            isLoading.value = false
            console.error(error)
            alert('May the browser did not support or there is some errors.')
        })
}

function stopCameraStream() {
    let tracks = camera.value.srcObject.getTracks()

    tracks.forEach((track) => {
        track.stop()
    })
}

function takePhoto() {
    if (!isPhotoTaken.value) {
        isShotPhoto.value = true

        const FLASH_TIMEOUT = 50

        setTimeout(() => {
            isShotPhoto.value = false
        }, FLASH_TIMEOUT)

        const context = canvas.value.getContext('2d')
        context.drawImage(camera.value, 0, 0, isMobileDevice ? 567 : 756, isMobileDevice ? 756 : 567)
        uploadImage()
        stopCameraStream()
    } else {
        emit('update:modelValue', [])
        createCameraElement()
    }
    isPhotoTaken.value = !isPhotoTaken.value
}

async function uploadImage() {
    const blobBin = atob(document.getElementById(canvasId).toDataURL('image/jpeg').split(',')[1])
    const array = []
    for (let i = 0; i < blobBin.length; i++) {
        array.push(blobBin.charCodeAt(i))
    }

    const file = new File([new Uint8Array(array)], 'capture.jpeg', { type: 'image/jpeg' })
    const res = await postUploadKycDocumentS3(file)
    emit('update:modelValue', [res.data])
}

function isMobile() {
    /* Storing user's device details in a variable*/
    let details = navigator.userAgent

    /* Creating a regular expression containing some mobile devices keywords to search it in details string */
    let regexp = /android|iphone|kindle|ipad/i

    /* Using test() method to search regexp in details it returns boolean value*/
    let isMobileDevice = regexp.test(details)

    return isMobileDevice
}
</script>

<style scoped></style>
